import { crudGetOne, CRUD_GET_ONE, ReduxState, useDelete, useGetOne, useUpdate } from "ra-core"
import { FC, useContext, createContext, useEffect } from "react"
import { push } from "connected-react-router"
import { useDispatch, useSelector } from "react-redux"
import { useCallback } from "react"
import { genUrlWithHashid } from "~/utils/hashid"
import { DeploymentResponseDto, DeploymentStatus } from "~/gen/api/deploy"
import { RecPatch } from "~/dataProvider/tools"
import chainOptions from "~/utils/chainOptions"
import { MutationCallbacks } from "~/dataProvider/types"
import { useSnackbar } from "notistack"
import retrieveAxiosErr from "~/utils/retrieveAxiosErr"
import { useLocation } from "react-router-dom"
import { crudGetOneEx } from "~/actions/crudFetchEx"


interface DeploymentDetailControllerProps {
  databases: DeploymentResponseDto[],
  currDatabase: DeploymentResponseDto | undefined,
  setCurrDatabase: (x: DeploymentResponseDto) => void,
  deleteRequesting: boolean,
  deleteDeployment: (ifBackup: boolean, options?: MutationCallbacks) => void,
  startRequesting: boolean,
  startDeployment: (options?: MutationCallbacks) => void,
  stopRequesting: boolean,
  stopDeployment: (options?: MutationCallbacks) => void,
}

const DeploymentDetailContext = createContext<DeploymentDetailControllerProps>({
  databases: [],
  currDatabase: undefined,
  setCurrDatabase: () => {},
  deleteRequesting: false,
  deleteDeployment: () => {},
  startRequesting: false,
  startDeployment: () => {},
  stopRequesting: false,
  stopDeployment: () => {},
})


export const DeploymentDetailContextProvider: FC<{ value: DeploymentDetailControllerProps }> = ({ children, value }) => {
  return (
    <DeploymentDetailContext.Provider value={value}>
      {children}
    </DeploymentDetailContext.Provider>
  )
}

export const useDeploymentDetailController = (presetDatabaseId: number): DeploymentDetailControllerProps => {
  const databases = 
    useSelector((store: ReduxState) => {
      const content = store.admin.resources['deployments/list']
      const ids = content && content.list.ids || []
      return ids.map(id => content.data[id] as DeploymentResponseDto & RecPatch).sort((a, b) => (a.name || '') < (b.name || '') ? -1 : 1)
    })

  const { data: currDatabase } = 
    useGetOne<DeploymentResponseDto & RecPatch>(databases.length === 0 ? 'noop' : 'deployments/list', presetDatabaseId, { action: CRUD_GET_ONE})

  const dispatch = useDispatch()
  const setCurrDatabaseAndEffect = useCallback((x: DeploymentResponseDto | null) => {
    if (x && x.deploymentId !== presetDatabaseId) {
      dispatch(push(genUrlWithHashid(`/deployments/list/$0/show`, x.deploymentId || 0, x.name)))
    }
  }, [presetDatabaseId, dispatch])

  const { enqueueSnackbar } = useSnackbar()
  const fromState = (useLocation().state as any)?.from

  const [deleteFn, { loading: deleteRequesting }] = useDelete('deployments/list')
  const deleteDeployment = useCallback((ifBackup: boolean, options?: MutationCallbacks) => {
    if (!currDatabase?.deploymentId) return;
    deleteFn('deployments/list', currDatabase.deploymentId, { ifBackup, data: currDatabase } as any, chainOptions(options, {
      onSuccess: () => {
        enqueueSnackbar('Delete deployment request received', { variant: 'info' })
        if (fromState === 'DatabaseDetail') {
          dispatch(push(genUrlWithHashid(`/databases/$0/show`, currDatabase.databaseId || 0)))
        } else {
          dispatch(push('/deployments/list'))
        }
      },
      onFailure: (err) => {
        const errMsg = retrieveAxiosErr(err)
        enqueueSnackbar(`Failed to delete deployment: ${errMsg}`, { variant: 'error' })
      },
    }))
  }, [currDatabase, dispatch, enqueueSnackbar])

  const [startFn, { loading: startRequesting }] = useUpdate('deployment-start')
  const startDeployment = useCallback((options?: MutationCallbacks) => {
    if (!currDatabase?.deploymentId || currDatabase.deploymentStatus !== DeploymentStatus.Stopped) return;
    startFn('deployment-start', currDatabase.deploymentId, undefined, null, chainOptions(options, {
      onSuccess: () => {
        enqueueSnackbar('Start deployment request received', { variant: 'info' })
        dispatch(syncDeployment(currDatabase, DeploymentStatus.Restarting))
      },
      onFailure: (err) => {
        const errMsg = retrieveAxiosErr(err)
        enqueueSnackbar(`Failed to start deployment: ${errMsg}`, { variant: 'error' })
      },
    }))
  }, [currDatabase, dispatch, enqueueSnackbar])
  
  const [stopFn, { loading: stopRequesting }] = useUpdate('deployment-stop')
  const stopDeployment = useCallback((options?: MutationCallbacks) => {
    if (!currDatabase?.deploymentId 
    || (currDatabase.deploymentStatus !== DeploymentStatus.Success
      && currDatabase.deploymentStatus !== DeploymentStatus.Warning))
          return;
    stopFn('deployment-stop', currDatabase.deploymentId, undefined, null, chainOptions(options, {
      onSuccess: () => {
        enqueueSnackbar('Stop deployment request received', { variant: 'info' })
        dispatch(syncDeployment(currDatabase, DeploymentStatus.Stopping))
      },
      onFailure: (err) => {
        const errMsg = retrieveAxiosErr(err)
        enqueueSnackbar(`Failed to stop deployment: ${errMsg}`, { variant: 'error' })
      },
    }))
  }, [currDatabase, dispatch, enqueueSnackbar])

  useEffect(() => {
    let timer: number | null = null
    if (currDatabase && ongoingStatus.some(x => x === currDatabase.deploymentStatus)) {
      timer = window.setInterval(() => {
        dispatch(crudGetOneEx('deployments/list', currDatabase.deploymentId || 0, ''))
      }, 15 * 1000)
    } else if (timer) {
      clearInterval(timer)
      timer = null
    }
    return () => { timer && clearInterval(timer) }
  }, [currDatabase?.deploymentId, currDatabase?.deploymentStatus])

  return {
    databases: databases,
    currDatabase, 
    setCurrDatabase: setCurrDatabaseAndEffect,
    deleteRequesting,
    deleteDeployment,
    startRequesting,
    startDeployment,
    stopRequesting,
    stopDeployment,
  }
}

export const useDeploymentDetailContext = () => useContext(DeploymentDetailContext);

const syncDeployment = (dto: DeploymentResponseDto, status: DeploymentStatus) => ({
  type: "OD/DEPLOYMENT_INFO_SYNC_SUCCESS",
  payload: { data: { ...dto, deploymentStatus: status }},
  requestPayload: { id: dto.deploymentId},
  meta: {
      resource: 'deployments/list',
      fetchResponse: 'GET_ONE',
      fetchStatus: 'RA/FETCH_END',
  },
})

const ongoingStatus = [
  DeploymentStatus.Deleting,
  DeploymentStatus.InProgress,
  DeploymentStatus.Restarting,
  DeploymentStatus.Stopping,
]
